import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import ToggleButton from "@mui/material/ToggleButton";
import Typography from "@mui/material/Typography";
import SelectFarmWarehouse, { getAOCWarehouses } from "../../components/SelectFarmWarehouse";
import { getAPIDataParamsReturn } from "../../utils/apiFunction";
import { numberFormatErrorToZero as numberFormat } from "../../utils/numberFunctions";
import {
    SLATS,
    buildOysterMateTotalsData,
    formatOysterMateQuantity,
    getBatchBackgroundColor,
    getOysterMateGradeColor,
    mergeOysterMateGroups,
} from "./oysterMateTotalsUtils";

const Batches = (props) => {
    const [farm, setFarm] = React.useState(null);
    const [allowedFarmList, setAllowedFarmList] = React.useState([]);
    const [allFarms, setAllFarms] = React.useState(false);
    const [inDoz, setInDoz] = React.useState(true);
    const [rawGroups, setRawGroups] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [loadError, setLoadError] = React.useState(null);
    const [loadedAt, setLoadedAt] = React.useState(null);

    React.useEffect(() => {
        getAOCWarehouses(props.access, props.showAll, setAllowedFarmList);
    }, [props.access, props.showAll]);

    React.useEffect(() => {
        const abortController = new AbortController();
        let isActive = true;

        const fetchGroups = async (warehouseId) => {
            const [waterGroups, onShoreGroups] = await Promise.all([
                getAPIDataParamsReturn("/Farms/Groups", { WarehouseId: warehouseId }, abortController),
                getAPIDataParamsReturn("/Farms/Groups/OnShore", { WarehouseId: warehouseId }, abortController),
            ]);

            return mergeOysterMateGroups(waterGroups, onShoreGroups);
        };

        const load = async () => {
            const warehouseIds = allFarms
                ? allowedFarmList.map((warehouse) => warehouse.warehouseId).filter(Boolean)
                : [farm?.warehouseId].filter(Boolean);

            if (warehouseIds.length === 0) {
                setRawGroups([]);
                setLoadError(null);
                return;
            }

            setLoadError(null);
            setLoading(true);
            try {
                const results = await Promise.all(warehouseIds.map((warehouseId) => fetchGroups(warehouseId)));
                if (!isActive) {
                    return;
                }
                setRawGroups(results.flat());
                setLoadError(null);
                setLoadedAt(new Date());
            } catch (error) {
                if (!isActive || abortController.signal.aborted || error?.name === "AbortError") {
                    return;
                }
                console.error(error);
                setRawGroups([]);
                setLoadError(error);
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            isActive = false;
            abortController.abort();
        };
    }, [farm?.warehouseId, allowedFarmList, allFarms]);

    const batchRows = React.useMemo(() => {
        return buildOysterMateTotalsData(rawGroups, "By Batch").filter((row) => row.totalEnd > 0);
    }, [rawGroups]);

    const gradeColourMap = React.useMemo(() => {
        const colours = {};
        let nextIndex = 0;

        batchRows.forEach((row) => {
            row.grade.forEach((grade) => {
                if (!colours[grade.description]) {
                    colours[grade.description] = getOysterMateGradeColor(grade, nextIndex);
                    nextIndex += 1;
                }
            });
        });

        return colours;
    }, [batchRows]);

    const totalOysters = React.useMemo(() => {
        return batchRows.reduce((sum, row) => sum + row.totalEnd, 0);
    }, [batchRows]);

    const maxTotal = React.useMemo(() => {
        return batchRows.reduce((max, row) => Math.max(max, row.totalEnd), 0);
    }, [batchRows]);

    const hasApproxTotal = React.useMemo(() => {
        return batchRows.some((row) => row.grade.some((grade) => grade.description === SLATS));
    }, [batchRows]);

    const loadedAtLabel = React.useMemo(() => {
        if (!loadedAt) {
            return "";
        }
        return `${loadedAt.toLocaleDateString("en-AU")} ${loadedAt.toLocaleTimeString("en-AU", {
            hour: "2-digit",
            minute: "2-digit",
        })}`;
    }, [loadedAt]);

    return (
        <Grid container spacing={2} mb={1}>
            <Grid item xs={12} md={6}>
                {!allFarms ? (
                    <SelectFarmWarehouse setState={setFarm} state={farm} access={props.access} />
                ) : (
                    <Typography style={{ display: "inline" }} variant="h2" mb={0}>
                        Summary of All Farms
                    </Typography>
                )}
                {allowedFarmList.length > 0 ? (
                    <ToggleButton
                        color="primary"
                        style={{ marginLeft: "20px" }}
                        selected={allFarms}
                        onChange={() => setAllFarms((current) => !current)}
                    >
                        <div style={{ whiteSpace: "nowrap" }}>All Farms</div>
                    </ToggleButton>
                ) : null}
            </Grid>
            <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                    <Typography>Pcs</Typography>
                    <Switch checked={inDoz} onChange={(event) => setInDoz(event.target.checked)} />
                    <Typography>Doz</Typography>
                </Stack>
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <CardHeader
                        title="Batches"
                        subheader={
                            loadError
                                ? "Unable to load live Oyster Mate totals."
                                : loadedAt
                                  ? `Live Oyster Mate totals refreshed ${loadedAtLabel}`
                                  : "Live Oyster Mate totals"
                        }
                        action={
                            <Stack spacing={0.25} alignItems={{ xs: "flex-start", sm: "flex-end" }}>
                                <Typography variant="body2" color="text.secondary" mb={0}>
                                    Active batches
                                </Typography>
                                <Typography variant="h5" mb={0}>
                                    {batchRows.length}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={0}>
                                    {(hasApproxTotal ? "≈" : "") + numberFormat(formatOysterMateQuantity(totalOysters, inDoz, false))} {inDoz ? "doz" : "pcs"}
                                </Typography>
                            </Stack>
                        }
                    />
                    <CardContent>
                        {loading ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={20} />
                                <Typography variant="body2">Loading live batch totals...</Typography>
                            </Stack>
                        ) : loadError ? (
                            <Typography color="error" variant="body2">
                                The live totals could not be loaded for the selected farm.
                            </Typography>
                        ) : batchRows.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                No live batches were found for the current selection.
                            </Typography>
                        ) : (
                            <Stack spacing={2}>
                                {batchRows.map((row) => {
                                    const grades = [...row.grade].sort((a, b) => (a.description || "").localeCompare(b.description || ""));
                                    const hasApprox = grades.some((grade) => grade.description === SLATS);

                                    return (
                                        <Box
                                            key={row.batch || "unknown"}
                                            sx={{
                                                backgroundColor: getBatchBackgroundColor(row.batch),
                                                border: "1px solid rgba(0, 0, 0, 0.08)",
                                                borderRadius: 2,
                                                p: 2,
                                            }}
                                        >
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} md={3}>
                                                    <Typography variant="h6" mb={0}>
                                                        {row.batch && row.batch.trim() !== "" ? row.batch : "Unknown"}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" mb={0}>
                                                        {grades.length} grade{grades.length === 1 ? "" : "s"}
                                                        {hasApprox ? " · includes slat estimate" : ""}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12} md={7}>
                                                    <Box
                                                        sx={{
                                                            height: 28,
                                                            overflow: "hidden",
                                                            borderRadius: 999,
                                                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                                                            border: "1px solid rgba(0, 0, 0, 0.1)",
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                height: "100%",
                                                                width: `${maxTotal > 0 ? (row.totalEnd / maxTotal) * 100 : 0}%`,
                                                                minWidth: row.totalEnd > 0 ? 12 : 0,
                                                            }}
                                                        >
                                                            {grades.map((grade) => {
                                                                const width = row.totalEnd > 0 ? (grade.end / row.totalEnd) * 100 : 0;
                                                                return (
                                                                    <Box
                                                                        key={`${row.batch || "unknown"}-${grade.description}`}
                                                                        sx={{
                                                                            width: `${width}%`,
                                                                            minWidth: width > 0 ? 4 : 0,
                                                                            backgroundColor: gradeColourMap[grade.description],
                                                                        }}
                                                                        title={`${grade.description}: ${(grade.description === SLATS ? "≈" : "") + numberFormat(formatOysterMateQuantity(grade.end, inDoz, false))} ${inDoz ? "doz" : "pcs"}`}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    </Box>
                                                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                                        {grades.map((grade) => (
                                                            <Stack
                                                                key={`${row.batch || "unknown"}-${grade.description}-legend`}
                                                                direction="row"
                                                                spacing={0.75}
                                                                alignItems="center"
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 10,
                                                                        height: 10,
                                                                        borderRadius: "50%",
                                                                        backgroundColor: gradeColourMap[grade.description],
                                                                        border: "1px solid rgba(0, 0, 0, 0.12)",
                                                                    }}
                                                                />
                                                                <Typography variant="caption" mb={0}>
                                                                    {grade.description}: {(grade.description === SLATS ? "≈" : "") + numberFormat(formatOysterMateQuantity(grade.end, inDoz, false))}
                                                                </Typography>
                                                            </Stack>
                                                        ))}
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={12} md={2}>
                                                    <Typography variant="h5" mb={0} align="right">
                                                        {(hasApprox ? "≈" : "") + numberFormat(formatOysterMateQuantity(row.totalEnd, inDoz, false))}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" mb={0} align="right">
                                                        {inDoz ? "doz" : "pcs"}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default Batches;